import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SimulationConfig, Emitter, Point } from './types';
import configService, { WavePoolPreset } from '../../services/configService';
import analyticsService from '../../services/analyticsService';

interface WavePoolConfigManagerProps {
  currentConfig: SimulationConfig;
  currentEmitters: Emitter[];
  currentFocusPoints?: Point[];
  onLoadConfig: (
    config: SimulationConfig,
    emitters: Emitter[],
    focusPoints?: Point[],
    meta?: { name?: string; description?: string; source?: 'saved' | 'preset'; tags?: string[] }
  ) => void;
  onConfigSaved?: () => void;
  mode?: 'saved' | 'save-dialog' | 'presets';
  onClose?: () => void;
  defaultName?: string;
  defaultDescription?: string;
}

export const WavePoolConfigManager: React.FC<WavePoolConfigManagerProps> = ({
  currentConfig,
  currentEmitters,
  currentFocusPoints,
  onLoadConfig,
  onConfigSaved,
  mode = 'saved',
  onClose,
  defaultName = '',
  defaultDescription = ''
}) => {
  const [savedConfigs, setSavedConfigs] = useState<any[]>([]);
  const [presetConfigs, setPresetConfigs] = useState<WavePoolPreset[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(mode === 'save-dialog');
  const [saveName, setSaveName] = useState(defaultName);
  const [saveDescription, setSaveDescription] = useState(defaultDescription);
  const [saveTags, setSaveTags] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);

  useEffect(() => {
    if (mode === 'saved') {
      loadSavedConfigs();
    } else if (mode === 'presets') {
      const loadPresets = async () => {
        setIsLoadingPresets(true);
        try {
          const presets = await configService.getWavePoolPresetConfigurations();
          setPresetConfigs(presets);
        } catch (error) {
          console.error('Failed to load WavePool presets:', error);
          setPresetConfigs([]);
        } finally {
          setIsLoadingPresets(false);
        }
      };
      loadPresets();
    }
  }, [mode]);

  const loadSavedConfigs = async () => {
    try {
      const configs = await configService.listWavePoolConfigs();
      setSavedConfigs(configs);
    } catch (error) {
      console.error('Failed to load Wave Pool configs:', error);
    }
  };

  const handleSaveConfig = async () => {
    if (!saveName.trim()) {
      alert('Please enter a configuration name');
      return;
    }

    try {
      setIsLoading(true);
      await configService.saveWavePoolConfig({
        name: saveName.trim(),
        description: saveDescription.trim(),
        poolConfig: currentConfig,
        emitters: currentEmitters,
        focusPoints: currentFocusPoints,
        tags: saveTags.split(',').map(tag => tag.trim()).filter(tag => tag)
      });

      analyticsService.trackFeatureUse('wavepool_config_save', `name:${saveName}`);

      setSaveName('');
      setSaveDescription('');
      setSaveTags('');
      setShowSaveDialog(false);
      onConfigSaved?.();
      await loadSavedConfigs();
    } catch (error) {
      console.error('Failed to save Wave Pool config:', error);
      analyticsService.trackError('wavepool_config_save_failed', String(error));
      alert('Failed to save configuration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadSavedConfig = (config: any) => {
    try {
      onLoadConfig(config.poolConfig, config.emitters, config.focusPoints, {
        name: config.name,
        description: config.description,
        source: 'saved',
        tags: config.tags
      });
      analyticsService.trackFeatureUse('wavepool_config_load', `config_id:${config.id}`);
    } catch (error) {
      console.error('Failed to load Wave Pool config:', error);
      analyticsService.trackError('wavepool_config_load_failed', String(error));
      alert('Failed to load configuration. Please try again.');
    }
  };

  const handleLoadPreset = (preset: WavePoolPreset) => {
    try {
      onLoadConfig(preset.poolConfig, preset.emitters, preset.focusPoints, {
        name: preset.name,
        description: preset.description,
        source: 'preset',
        tags: preset.tags
      });
      analyticsService.trackFeatureUse('wavepool_preset_load', preset.id);
    } catch (error) {
      console.error('Failed to load Wave Pool preset:', error);
      analyticsService.trackError('wavepool_preset_load_failed', String(error));
      alert('Failed to load preset. Please try again.');
    }
  };

  const handleDeleteConfig = async (configId: string) => {
    if (!confirm('Are you sure you want to delete this configuration?')) return;

    try {
      await configService.deleteWavePoolConfig(configId);
      analyticsService.trackFeatureUse('wavepool_config_delete');
      await loadSavedConfigs();
    } catch (error) {
      console.error('Failed to delete Wave Pool config:', error);
      alert('Failed to delete configuration. Please try again.');
    }
  };

  // Mode: save-dialog - Show only the save dialog modal
  if (mode === 'save-dialog') {
    return createPortal(
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto"
        style={{ zIndex: 9999 }}
        onClick={() => onClose?.()}
      >
        <div
          className="bg-white rounded-lg p-6 w-full max-w-md my-8 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Save Wave Pool Configuration</h2>
            <button
              onClick={() => onClose?.()}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="My Wave Pool Configuration"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={saveDescription}
                onChange={(e) => setSaveDescription(e.target.value)}
                placeholder="Optional description..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <input
                type="text"
                value={saveTags}
                onChange={(e) => setSaveTags(e.target.value)}
                placeholder="tag1, tag2, tag3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => onClose?.()}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfig}
                disabled={isLoading || !saveName.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Mode: saved - Show user's saved configurations
  const filteredConfigs = savedConfigs.filter(config => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const matchesName = config.name.toLowerCase().includes(query);
    const matchesDescription = config.description?.toLowerCase().includes(query);
    const matchesTags = config.tags?.some((tag: string) => tag.toLowerCase().includes(query));
    return matchesName || matchesDescription || matchesTags;
  });


  if (mode === 'presets') {
    return (
      <div className="space-y-1">
        <div className="px-3 py-2 bg-white border-b border-slate-200 sticky top-0 z-10">
          <input
            type="text"
            placeholder="Filter presets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {isLoadingPresets ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
          {Object.entries(
            presetConfigs.filter(preset => {
              if (!searchQuery.trim()) return true;
              const query = searchQuery.toLowerCase();
              const matchesName = preset.name.toLowerCase().includes(query);
              const matchesDescription = preset.description?.toLowerCase().includes(query);
              const matchesTags = preset.tags?.some(tag => tag.toLowerCase().includes(query));
              return matchesName || matchesDescription || matchesTags;
            })
            .reduce((acc, preset) => {
              const category = preset.category || 'Other';
              if (!acc[category]) acc[category] = [];
              acc[category].push(preset);
              return acc;
            }, {} as Record<string, WavePoolPreset[]>)
          ).map(([category, presets]) => (
          <div key={category} className="border-b border-slate-200 last:border-b-0">
            <div className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-widest bg-gray-50">
              {category}
            </div>
            {presets.map((preset) => (
              <div key={preset.id} className="flex justify-between items-start p-3 hover:bg-gray-50">
                <div className="flex-1 pr-2 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-gray-900 text-sm truncate">{preset.name}</h3>
                    <span className="text-xs text-gray-500 whitespace-nowrap">{preset.poolConfig.pattern}</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2 line-clamp-2">{preset.description}</p>
                  {preset.tags && preset.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {preset.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[11px] rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleLoadPreset(preset)}
                  className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors flex-shrink-0"
                >
                  Load
                </button>
              </div>
            ))}
          </div>
        ))}
        </>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {/* Filter/Search Box */}
      <div className="px-4 pt-4 pb-2 bg-white sticky top-0 z-10 border-b border-gray-200">
        <input
          type="text"
          placeholder="Filter configurations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="px-4 pb-4 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredConfigs.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            {savedConfigs.length === 0
              ? 'No saved configurations found.'
              : 'No configurations match your search.'}
          </div>
        ) : (
          filteredConfigs.map((config) => (
            <div key={config.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 text-sm truncate mb-1">{config.name}</h3>
                  <p className="text-xs text-gray-600 mb-2">{config.description}</p>
                  <div className="flex items-center space-x-2 text-xs text-gray-500 mb-2">
                    <span>{config.poolConfig?.pattern || 'Unknown'} pattern</span>
                  </div>
                  {config.tags && config.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {config.tags.map((tag: string, tagIndex: number) => (
                        <span
                          key={tagIndex}
                          className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col space-y-1 flex-shrink-0">
                  <button
                    onClick={() => handleLoadSavedConfig(config)}
                    className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs whitespace-nowrap"
                  >
                    Load
                  </button>
                  <button
                    onClick={() => handleDeleteConfig(config.id)}
                    className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs whitespace-nowrap"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default WavePoolConfigManager;
